// File Location: src/screens/dashboard/RecoveryTracking.js

import React, { useState, useEffect, useCallback, useRef } from 'react';
import { createPortal } from 'react-dom';
import {
  Banknote,
  Clock,
  CheckCircle,
  XCircle,
  AlertCircle,
  DollarSign,
  Calendar,
  User,
  Search,
  RefreshCw,
  Edit,
  Eye,
  FileText,
  Target,
  Percent,
  CreditCard
} from 'lucide-react';

import { useAuth } from '../../context/AuthContext';
import {
  getFinancialApplications,
  updateRecoveryStatus
} from '../../services/financialservice';

// Standalone, memoized modal rendered via portal to keep identity stable and avoid focus loss
const UpdateRecoveryModal = React.memo(function UpdateRecoveryModal({
  selectedRecovery,
  updateData,
  setUpdateData,
  onClose,
  onSubmit,
  addFollowUpAction,
  updateFollowUpAction,
  removeFollowUpAction
}) {
  const firstFieldRef = useRef(null);

  useEffect(() => {
    firstFieldRef.current?.focus();
  }, []);

  // Stable change handlers
  const handleStatusChange = useCallback((value) => {
    setUpdateData(prev => ({ ...prev, status: value }));
  }, [setUpdateData]);

  const handleRecoveredAmountChange = useCallback((value) => {
    setUpdateData(prev => ({ ...prev, recoveredAmount: value }));
  }, [setUpdateData]);

  const handleRecoveryDateChange = useCallback((value) => {
    setUpdateData(prev => ({ ...prev, recoveryDate: value }));
  }, [setUpdateData]);

  const handleInsuranceResponseDateChange = useCallback((value) => {
    setUpdateData(prev => ({ ...prev, insuranceResponseDate: value }));
  }, [setUpdateData]);

  const handleEnachStatusChange = useCallback((value) => {
    setUpdateData(prev => ({ ...prev, enachStatus: value }));
  }, [setUpdateData]);

  const handleEnachFieldChange = useCallback((field, value) => {
    setUpdateData(prev => ({
      ...prev,
      enachDetails: { ...prev.enachDetails, [field]: value }
    }));
  }, [setUpdateData]);

  const handleInsuranceFieldChange = useCallback((field, value) => {
    setUpdateData(prev => ({
      ...prev,
      insurance: { ...prev.insurance, [field]: value }
    }));
  }, [setUpdateData]);

  const handleUserDisbursalFieldChange = useCallback((field, value) => {
    setUpdateData(prev => ({
      ...prev,
      userDisbursal: { ...prev.userDisbursal, [field]: value }
    }));
  }, [setUpdateData]);

  const handleLenderCommunicationFieldChange = useCallback((field, value) => {
    setUpdateData(prev => ({
      ...prev,
      lenderCommunication: { ...prev.lenderCommunication, [field]: value }
    }));
  }, [setUpdateData]);

  const handleRecoveryNotesChange = useCallback((value) => {
    setUpdateData(prev => ({ ...prev, recoveryNotes: value }));
  }, [setUpdateData]);

  if (!selectedRecovery) return null;

  const modal = (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4" role="dialog" aria-modal="true">
      <div className="bg-white rounded-lg shadow-xl max-w-6xl w-full max-h-[90vh] overflow-y-auto overscroll-contain" onWheel={(e) => e.stopPropagation()}>
        <div className="p-6 border-b border-gray-200">
          <div className="flex items-center justify-between">
            <h2 className="text-xl font-semibold text-gray-900">Update Recovery Status</h2>
            <button onClick={onClose} className="text-gray-400 hover:text-gray-600" aria-label="Close">
              <XCircle className="h-6 w-6" />
            </button>
          </div>
          <p className="text-sm text-gray-600 mt-1">
            Application ID: {selectedRecovery.applicationId} | Patient: {selectedRecovery.patient?.name}
          </p>
        </div>

        <div className="p-6 space-y-6">
          {/* Recovery Status */}
          <div className="bg-blue-50 rounded-lg p-4">
            <h3 className="text-lg font-medium text-gray-900 mb-4">Recovery Status</h3>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Recovery Status</label>
                <select
                  ref={firstFieldRef}
                  value={updateData.status}
                  onChange={(e) => handleStatusChange(e.target.value)}
                  className="w-full border border-gray-300 rounded-md px-3 py-2 focus:ring-2 focus:ring-teal-500"
                >
                  <option value="pending">Pending Recovery</option>
                  <option value="in_progress">In Progress</option>
                  <option value="partial_recovery">Partial Recovery</option>
                  <option value="completed">Completed</option>
                  <option value="overdue">Overdue</option>
                  <option value="disputed">Disputed</option>
                </select>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Recovered Amount</label>
                <input
                  type="number"
                  value={updateData.recoveredAmount}
                  onChange={(e) => handleRecoveredAmountChange(e.target.value)}
                  className="w-full border border-gray-300 rounded-md px-3 py-2 focus:ring-2 focus:ring-teal-500"
                  placeholder="Enter recovered amount"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Recovery Date</label>
                <input
                  type="date"
                  value={updateData.recoveryDate}
                  onChange={(e) => handleRecoveryDateChange(e.target.value)}
                  className="w-full border border-gray-300 rounded-md px-3 py-2 focus:ring-2 focus:ring-teal-500"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Insurance Response Date</label>
                <input
                  type="date"
                  value={updateData.insuranceResponseDate}
                  onChange={(e) => handleInsuranceResponseDateChange(e.target.value)}
                  className="w-full border border-gray-300 rounded-md px-3 py-2 focus:ring-2 focus:ring-teal-500"
                />
              </div>
            </div>
          </div>

          {/* eNACH Details */}
          <div className="bg-green-50 rounded-lg p-4">
            <h3 className="text-lg font-medium text-gray-900 mb-4">eNACH Setup</h3>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">eNACH Status</label>
                <select
                  value={updateData.enachStatus}
                  onChange={(e) => handleEnachStatusChange(e.target.value)}
                  className="w-full border border-gray-300 rounded-md px-3 py-2 focus:ring-2 focus:ring-teal-500"
                >
                  <option value="">Not Applicable</option>
                  <option value="pending">Pending Setup</option>
                  <option value="active">Active</option>
                  <option value="completed">Completed</option>
                  <option value="failed">Failed</option>
                  <option value="cancelled">Cancelled</option>
                </select>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Mandate ID</label>
                <input
                  type="text"
                  value={updateData.enachDetails.mandateId}
                  onChange={(e) => handleEnachFieldChange('mandateId', e.target.value)}
                  className="w-full border border-gray-300 rounded-md px-3 py-2 focus:ring-2 focus:ring-teal-500"
                  placeholder="Enter mandate ID"
                />
              </div>
            </div>

            {updateData.enachStatus && updateData.enachStatus !== '' && (
              <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Bank Name</label>
                  <input
                    type="text"
                    value={updateData.enachDetails.bankName}
                    onChange={(e) => handleEnachFieldChange('bankName', e.target.value)}
                    className="w-full border border-gray-300 rounded-md px-3 py-2 focus:ring-2 focus:ring-teal-500"
                    placeholder="Enter bank name"
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Monthly Amount</label>
                  <input
                    type="number"
                    value={updateData.enachDetails.monthlyAmount}
                    onChange={(e) => handleEnachFieldChange('monthlyAmount', e.target.value)}
                    className="w-full border border-gray-300 rounded-md px-3 py-2 focus:ring-2 focus:ring-teal-500"
                    placeholder="Monthly deduction amount"
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Total Installments</label>
                  <input
                    type="number"
                    value={updateData.enachDetails.totalInstallments}
                    onChange={(e) => handleEnachFieldChange('totalInstallments', e.target.value)}
                    className="w-full border border-gray-300 rounded-md px-3 py-2 focus:ring-2 focus:ring-teal-500"
                    placeholder="Number of installments"
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Start Date</label>
                  <input
                    type="date"
                    value={updateData.enachDetails.startDate}
                    onChange={(e) => handleEnachFieldChange('startDate', e.target.value)}
                    className="w-full border border-gray-300 rounded-md px-3 py-2 focus:ring-2 focus:ring-teal-500"
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">End Date</label>
                  <input
                    type="date"
                    value={updateData.enachDetails.endDate}
                    onChange={(e) => handleEnachFieldChange('endDate', e.target.value)}
                    className="w-full border border-gray-300 rounded-md px-3 py-2 focus:ring-2 focus:ring-teal-500"
                  />
                </div>
              </div>
            )}
          </div>

          {/* Insurance Process */}
          <div className="bg-indigo-50 rounded-lg p-4">
            <h3 className="text-lg font-medium text-gray-900 mb-4">Insurance Process</h3>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Insurance Status</label>
                <select
                  value={updateData.insurance.status}
                  onChange={(e) => handleInsuranceFieldChange('status', e.target.value)}
                  className="w-full border border-gray-300 rounded-md px-3 py-2 focus:ring-2 focus:ring-teal-500"
                >
                  <option value="">Select</option>
                  <option value="under_process">Under Process by Insurance</option>
                  <option value="approved">Approved</option>
                  <option value="rejected">Rejected</option>
                </select>
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Claim Number</label>
                <input
                  type="text"
                  value={updateData.insurance.claimNumber}
                  onChange={(e) => handleInsuranceFieldChange('claimNumber', e.target.value)}
                  className="w-full border border-gray-300 rounded-md px-3 py-2"
                  placeholder="Insurer claim/reference"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Amount Approved</label>
                <input
                  type="number"
                  value={updateData.insurance.amountApproved}
                  onChange={(e) => handleInsuranceFieldChange('amountApproved', e.target.value)}
                  className="w-full border border-gray-300 rounded-md px-3 py-2"
                  placeholder="₹"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Response Date</label>
                <input
                  type="date"
                  value={updateData.insurance.responseDate}
                  onChange={(e) => handleInsuranceFieldChange('responseDate', e.target.value)}
                  className="w-full border border-gray-300 rounded-md px-3 py-2"
                />
              </div>
              <div className="md:col-span-2">
                <label className="block text-sm font-medium text-gray-700 mb-1">Insurance Notes</label>
                <input
                  type="text"
                  value={updateData.insurance.notes}
                  onChange={(e) => handleInsuranceFieldChange('notes', e.target.value)}
                  className="w-full border border-gray-300 rounded-md px-3 py-2"
                  placeholder="Notes about insurer process"
                />
              </div>
            </div>
          </div>

          {/* Amount Disbursed to User */}
          <div className="bg-teal-50 rounded-lg p-4">
            <h3 className="text-lg font-medium text-gray-900 mb-4">Amount Disbursed to User</h3>
            <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Status</label>
                <select
                  value={updateData.userDisbursal.status}
                  onChange={(e) => handleUserDisbursalFieldChange('status', e.target.value)}
                  className="w-full border border-gray-300 rounded-md px-3 py-2"
                >
                  <option value="">Select</option>
                  <option value="disbursed">Disbursed</option>
                  <option value="pending">Pending</option>
                </select>
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Amount</label>
                <input
                  type="number"
                  value={updateData.userDisbursal.amount}
                  onChange={(e) => handleUserDisbursalFieldChange('amount', e.target.value)}
                  className="w-full border border-gray-300 rounded-md px-3 py-2"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Date</label>
                <input
                  type="date"
                  value={updateData.userDisbursal.date}
                  onChange={(e) => handleUserDisbursalFieldChange('date', e.target.value)}
                  className="w-full border border-gray-300 rounded-md px-3 py-2"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Mode</label>
                <input
                  type="text"
                  value={updateData.userDisbursal.mode}
                  onChange={(e) => handleUserDisbursalFieldChange('mode', e.target.value)}
                  className="w-full border border-gray-300 rounded-md px-3 py-2"
                  placeholder="NEFT/UPI/IMPS"
                />
              </div>
              <div className="md:col-span-2">
                <label className="block text-sm font-medium text-gray-700 mb-1">Reference</label>
                <input
                  type="text"
                  value={updateData.userDisbursal.reference}
                  onChange={(e) => handleUserDisbursalFieldChange('reference', e.target.value)}
                  className="w-full border border-gray-300 rounded-md px-3 py-2"
                  placeholder="Txn reference"
                />
              </div>
            </div>
          </div>

          {/* Lender Communication */}
          <div className="bg-cyan-50 rounded-lg p-4">
            <h3 className="text-lg font-medium text-gray-900 mb-4">Lender Communication</h3>
            <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
              <div className="flex items-center space-x-2">
                <input
                  type="checkbox"
                  checked={updateData.lenderCommunication.informed}
                  onChange={(e) => handleLenderCommunicationFieldChange('informed', e.target.checked)}
                  className="rounded border-gray-300 text-teal-600"
                />
                <span className="text-sm text-gray-700">Informed Lender</span>
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Informed At</label>
                <input
                  type="datetime-local"
                  value={updateData.lenderCommunication.informedAt}
                  onChange={(e) => handleLenderCommunicationFieldChange('informedAt', e.target.value)}
                  className="w-full border border-gray-300 rounded-md px-3 py-2"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Contact Person</label>
                <input
                  type="text"
                  value={updateData.lenderCommunication.contactPerson}
                  onChange={(e) => handleLenderCommunicationFieldChange('contactPerson', e.target.value)}
                  className="w-full border border-gray-300 rounded-md px-3 py-2"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Channel</label>
                <input
                  type="text"
                  value={updateData.lenderCommunication.channel}
                  onChange={(e) => handleLenderCommunicationFieldChange('channel', e.target.value)}
                  className="w-full border border-gray-300 rounded-md px-3 py-2"
                  placeholder="Email/Call/Portal"
                />
              </div>
              <div className="md:col-span-2">
                <label className="block text-sm font-medium text-gray-700 mb-1">Notes</label>
                <input
                  type="text"
                  value={updateData.lenderCommunication.notes}
                  onChange={(e) => handleLenderCommunicationFieldChange('notes', e.target.value)}
                  className="w-full border border-gray-300 rounded-md px-3 py-2"
                />
              </div>
              <div className="flex items-center space-x-2">
                <input
                  type="checkbox"
                  checked={updateData.lenderCommunication.enachInitiate}
                  onChange={(e) => handleLenderCommunicationFieldChange('enachInitiate', e.target.checked)}
                  className="rounded border-gray-300 text-teal-600"
                />
                <span className="text-sm text-gray-700">Tell lender to initiate eNACH</span>
              </div>
            </div>
          </div>

          {/* Follow-up Actions */}
          <div className="bg-yellow-50 rounded-lg p-4">
            <div className="flex items-center justify-between mb-4">
              <h3 className="text-lg font-medium text-gray-900">Follow-up Actions</h3>
              <button
                onClick={addFollowUpAction}
                className="flex items-center space-x-1 px-3 py-2 bg-yellow-600 text-white rounded-md hover:bg-yellow-700 text-sm"
              >
                <FileText className="h-4 w-4" />
                <span>Add Follow-up</span>
              </button>
            </div>

            {updateData.followUpActions.length === 0 ? (
              <p className="text-gray-600 text-center py-4">No follow-up actions recorded</p>
            ) : (
              <div className="space-y-3">
                {updateData.followUpActions.map((action) => (
                  <div key={action.id} className="bg-white p-4 rounded-lg border border-yellow-200">
                    <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-1">Date</label>
                        <input
                          type="date"
                          value={action.date}
                          onChange={(e) => updateFollowUpAction(action.id, 'date', e.target.value)}
                          className="w-full border border-gray-300 rounded-md px-3 py-2 text-sm"
                        />
                      </div>

                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-1">Action Type</label>
                        <select
                          value={action.action}
                          onChange={(e) => updateFollowUpAction(action.id, 'action', e.target.value)}
                          className="w-full border border-gray-300 rounded-md px-3 py-2 text-sm"
                        >
                          <option value="">Select action</option>
                          <option value="phone_call">Phone Call</option>
                          <option value="email_sent">Email Sent</option>
                          <option value="letter_sent">Letter Sent</option>
                          <option value="meeting_scheduled">Meeting Scheduled</option>
                          <option value="legal_notice">Legal Notice</option>
                          <option value="enach_setup">eNACH Setup</option>
                          <option value="payment_received">Payment Received</option>
                          <option value="other">Other</option>
                        </select>
                      </div>

                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-1">Next Follow-up</label>
                        <input
                          type="date"
                          value={action.nextFollowUp}
                          onChange={(e) => updateFollowUpAction(action.id, 'nextFollowUp', e.target.value)}
                          className="w-full border border-gray-300 rounded-md px-3 py-2 text-sm"
                        />
                      </div>

                      <div className="md:col-span-2">
                        <label className="block text-sm font-medium text-gray-700 mb-1">Notes</label>
                        <textarea
                          value={action.notes}
                          onChange={(e) => updateFollowUpAction(action.id, 'notes', e.target.value)}
                          rows={2}
                          className="w-full border border-gray-300 rounded-md px-3 py-2 text-sm"
                          placeholder="Enter notes about this follow-up action"
                        />
                      </div>

                      <div className="flex items-end">
                        <button
                          onClick={() => removeFollowUpAction(action.id)}
                          className="w-full px-3 py-2 bg-red-600 text-white rounded-md hover:bg-red-700 text-sm"
                        >
                          Remove
                        </button>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>

          {/* Recovery Notes */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Recovery Notes</label>
            <textarea
              value={updateData.recoveryNotes}
              onChange={(e) => handleRecoveryNotesChange(e.target.value)}
              rows={4}
              className="w-full border border-gray-300 rounded-md px-3 py-2 focus:ring-2 focus:ring-teal-500"
              placeholder="Add notes about recovery progress, challenges, or next steps..."
            />
          </div>
        </div>

        <div className="p-6 border-t border-gray-200 flex justify-end space-x-3">
          <button
            onClick={onClose}
            className="px-4 py-2 text-gray-700 bg-gray-200 rounded-lg hover:bg-gray-300 transition-colors"
          >
            Cancel
          </button>
          <button
            onClick={onSubmit}
            className="px-6 py-2 bg-teal-600 text-white rounded-lg hover:bg-teal-700 transition-colors"
          >
            Update Recovery
          </button>
        </div>
      </div>
    </div>
  );

  return createPortal(modal, document.body);
});

const RecoveryTracking = () => {
  const { userData: user } = useAuth();
  const [recoveries, setRecoveries] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedRecovery, setSelectedRecovery] = useState(null);
  const [showUpdateModal, setShowUpdateModal] = useState(false);
  const [stats, setStats] = useState({
    totalRecoveryAmount: 0,
    recoveredAmount: 0,
    pendingAmount: 0,
    overdueAmount: 0,
    recoveryRate: 0
  });

  const [filters, setFilters] = useState({
    status: '',
    insuranceCompany: '',
    searchTerm: '',
    dateFrom: '',
    dateTo: '',
    overdueOnly: false
  });

  const [updateData, setUpdateData] = useState({
    status: '',
    recoveredAmount: '',
    recoveryDate: '',
    remainingAmount: '',
    insuranceResponseDate: '',
    recoveryNotes: '',
    enachStatus: '',
    enachDetails: {
      mandateId: '',
      bankName: '',
      accountNumber: '',
      monthlyAmount: '',
      startDate: '',
      endDate: '',
      totalInstallments: ''
    },
    // New structured tracking fields
    insurance: {
      status: '', // under_process | approved | rejected
      claimNumber: '',
      amountApproved: '',
      responseDate: '',
      notes: ''
    },
    userDisbursal: {
      status: '', // disbursed | pending
      amount: '',
      date: '',
      mode: '',
      reference: ''
    },
    lenderCommunication: {
      informed: false,
      informedAt: '',
      contactPerson: '',
      channel: '',
      notes: '',
      enachInitiate: false
    },
    followUpActions: []
  });

  // Load recovery data
  const loadRecoveries = useCallback(async () => {
    setLoading(true);
    try {
      // Get applications that have been disbursed and need recovery tracking
      const result = await getFinancialApplications({
        ...filters,
        status: 'amount_disbursed' // Applications where amount has been disbursed
      }, 1, 50);
      
      if (result.success) {
        // Filter for applications with recovery workflow
        const recoveryApplications = result.applications.filter(app => 
          app.workflow?.disbursal?.status === 'disbursed' ||
          app.workflow?.recovery?.status
        );
        setRecoveries(recoveryApplications);
        calculateStats(recoveryApplications);
      }
    } catch (error) {
      console.error('Error loading recoveries:', error);
    } finally {
      setLoading(false);
    }
  }, [filters]);

  const calculateStats = (applications) => {
    let totalRecoveryAmount = 0;
    let recoveredAmount = 0;
    let pendingAmount = 0;
    let overdueAmount = 0;

    applications.forEach(app => {
      const disbursedAmount = app.workflow?.disbursal?.disbursedAmount || 0;
      const recoveryData = app.workflow?.recovery || {};
      const recovered = recoveryData.recoveredAmount || 0;
      
      totalRecoveryAmount += disbursedAmount;
      recoveredAmount += recovered;
      
      if (recoveryData.status === 'completed') {
        // Already recovered
      } else {
        const remaining = disbursedAmount - recovered;
        pendingAmount += remaining;
        
        // Check if overdue (more than 90 days since disbursal)
        const disbursedDate = app.workflow?.disbursal?.disbursedDate;
        if (disbursedDate) {
          const daysSinceDisbursal = Math.floor((new Date() - new Date(disbursedDate)) / (1000 * 60 * 60 * 24));
          if (daysSinceDisbursal > 90) {
            overdueAmount += remaining;
          }
        }
      }
    });

    const recoveryRate = totalRecoveryAmount > 0 ? (recoveredAmount / totalRecoveryAmount) * 100 : 0;

    setStats({
      totalRecoveryAmount,
      recoveredAmount,
      pendingAmount,
      overdueAmount,
      recoveryRate
    });
  };

  useEffect(() => {
    loadRecoveries();
  }, [loadRecoveries]);

  const formatCurrency = (amount) => {
    if (!amount) return '₹0';
    return `₹${amount.toLocaleString('en-IN')}`;
  };

  const formatDate = (date) => {
    if (!date) return '-';
    const dateObj = date.toDate ? date.toDate() : new Date(date);
    return dateObj.toLocaleDateString('en-IN', {
      day: '2-digit',
      month: 'short',
      year: 'numeric'
    });
  };

  const getStatusBadge = (status) => {
    const statusConfig = {
      'pending': { color: 'bg-yellow-100 text-yellow-800', text: 'Pending Recovery' },
      'partial_recovery': { color: 'bg-blue-100 text-blue-800', text: 'Partial Recovery' },
      'in_progress': { color: 'bg-orange-100 text-orange-800', text: 'In Progress' },
      'completed': { color: 'bg-green-100 text-green-800', text: 'Completed' },
      'overdue': { color: 'bg-red-100 text-red-800', text: 'Overdue' },
      'disputed': { color: 'bg-purple-100 text-purple-800', text: 'Disputed' }
    };

    const config = statusConfig[status] || { color: 'bg-gray-100 text-gray-800', text: status };
    
    return (
      <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${config.color}`}>
        {config.text}
      </span>
    );
  };

  const getDaysOverdue = (disbursedDate) => {
    if (!disbursedDate) return 0;
    const daysSince = Math.floor((new Date() - new Date(disbursedDate)) / (1000 * 60 * 60 * 24));
    return Math.max(0, daysSince - 90); // 90 days is standard recovery period
  };

  const openUpdateModal = (recovery) => {
    setSelectedRecovery(recovery);
    
    // Pre-populate with existing data
    const recoveryData = recovery.workflow?.recovery || {};
    
    setUpdateData({
      status: recoveryData.status || 'pending',
      recoveredAmount: recoveryData.recoveredAmount || '',
      recoveryDate: recoveryData.recoveryDate || '',
      remainingAmount: recoveryData.remainingAmount || '',
      insuranceResponseDate: recoveryData.insuranceResponseDate || '',
      recoveryNotes: recoveryData.recoveryNotes || '',
      enachStatus: recoveryData.enachStatus || '',
      enachDetails: {
        mandateId: recoveryData.enachDetails?.mandateId || '',
        bankName: recoveryData.enachDetails?.bankName || '',
        accountNumber: recoveryData.enachDetails?.accountNumber || '',
        monthlyAmount: recoveryData.enachDetails?.monthlyAmount || '',
        startDate: recoveryData.enachDetails?.startDate || '',
        endDate: recoveryData.enachDetails?.endDate || '',
        totalInstallments: recoveryData.enachDetails?.totalInstallments || ''
      },
      insurance: {
        status: recoveryData.insurance?.status || '',
        claimNumber: recoveryData.insurance?.claimNumber || '',
        amountApproved: recoveryData.insurance?.amountApproved || '',
        responseDate: recoveryData.insurance?.responseDate || '',
        notes: recoveryData.insurance?.notes || ''
      },
      userDisbursal: {
        status: recoveryData.userDisbursal?.status || '',
        amount: recoveryData.userDisbursal?.amount || '',
        date: recoveryData.userDisbursal?.date || '',
        mode: recoveryData.userDisbursal?.mode || '',
        reference: recoveryData.userDisbursal?.reference || ''
      },
      lenderCommunication: {
        informed: recoveryData.lenderCommunication?.informed || false,
        informedAt: recoveryData.lenderCommunication?.informedAt || '',
        contactPerson: recoveryData.lenderCommunication?.contactPerson || '',
        channel: recoveryData.lenderCommunication?.channel || '',
        notes: recoveryData.lenderCommunication?.notes || '',
        enachInitiate: recoveryData.lenderCommunication?.enachInitiate || false
      },
      followUpActions: recoveryData.followUpActions || []
    });
    
    setShowUpdateModal(true);
  };

  const handleUpdateSubmit = async () => {
    if (!selectedRecovery || !user?.uid) return;

    try {
      const result = await updateRecoveryStatus(
        selectedRecovery.applicationId,
        user.uid,
        updateData
      );

      if (result.success) {
        setShowUpdateModal(false);
        setSelectedRecovery(null);
        loadRecoveries();
        alert('Recovery status updated successfully!');
      } else {
        alert('Error updating recovery: ' + result.error);
      }
    } catch (error) {
      console.error('Error updating recovery:', error);
      alert('Error updating recovery status');
    }
  };

  const addFollowUpAction = () => {
    setUpdateData(prev => ({
      ...prev,
      followUpActions: [
        ...prev.followUpActions,
        {
          id: Date.now(),
          date: new Date().toISOString().split('T')[0],
          action: '',
          notes: '',
          nextFollowUp: ''
        }
      ]
    }));
  };

  const updateFollowUpAction = (id, field, value) => {
    setUpdateData(prev => ({
      ...prev,
      followUpActions: prev.followUpActions.map(action =>
        action.id === id ? { ...action, [field]: value } : action
      )
    }));
  };

  const removeFollowUpAction = (id) => {
    setUpdateData(prev => ({
      ...prev,
      followUpActions: prev.followUpActions.filter(action => action.id !== id)
    }));
  };

  // Stable onClose to clear selection and hide modal
  const handleModalClose = useCallback(() => {
    setShowUpdateModal(false);
    setSelectedRecovery(null);
  }, []);

  // Lock background scroll when modal is open
  useEffect(() => {
    if (showUpdateModal) {
      const prev = document.body.style.overflow;
      document.body.style.overflow = 'hidden';
      return () => { document.body.style.overflow = prev; };
    }
  }, [showUpdateModal]);

  const StatsCards = () => (
    <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-5 gap-6 mb-8">
      <div className="bg-white p-6 rounded-lg shadow-sm border border-gray-200">
        <div className="flex items-center justify-between">
          <div>
            <p className="text-sm font-medium text-gray-600">Total Recovery Amount</p>
            <p className="text-2xl font-bold text-blue-600">
              {formatCurrency(stats.totalRecoveryAmount)}
            </p>
          </div>
          <div className="bg-blue-100 p-3 rounded-lg">
            <Target className="h-6 w-6 text-blue-600" />
          </div>
        </div>
      </div>

      <div className="bg-white p-6 rounded-lg shadow-sm border border-gray-200">
        <div className="flex items-center justify-between">
          <div>
            <p className="text-sm font-medium text-gray-600">Recovered Amount</p>
            <p className="text-2xl font-bold text-green-600">
              {formatCurrency(stats.recoveredAmount)}
            </p>
          </div>
          <div className="bg-green-100 p-3 rounded-lg">
            <CheckCircle className="h-6 w-6 text-green-600" />
          </div>
        </div>
      </div>

      <div className="bg-white p-6 rounded-lg shadow-sm border border-gray-200">
        <div className="flex items-center justify-between">
          <div>
            <p className="text-sm font-medium text-gray-600">Pending Recovery</p>
            <p className="text-2xl font-bold text-yellow-600">
              {formatCurrency(stats.pendingAmount)}
            </p>
          </div>
          <div className="bg-yellow-100 p-3 rounded-lg">
            <Clock className="h-6 w-6 text-yellow-600" />
          </div>
        </div>
      </div>

      <div className="bg-white p-6 rounded-lg shadow-sm border border-gray-200">
        <div className="flex items-center justify-between">
          <div>
            <p className="text-sm font-medium text-gray-600">Overdue Amount</p>
            <p className="text-2xl font-bold text-red-600">
              {formatCurrency(stats.overdueAmount)}
            </p>
          </div>
          <div className="bg-red-100 p-3 rounded-lg">
            <AlertCircle className="h-6 w-6 text-red-600" />
          </div>
        </div>
      </div>

      <div className="bg-white p-6 rounded-lg shadow-sm border border-gray-200">
        <div className="flex items-center justify-between">
          <div>
            <p className="text-sm font-medium text-gray-600">Recovery Rate</p>
            <p className="text-2xl font-bold text-purple-600">
              {stats.recoveryRate.toFixed(1)}%
            </p>
          </div>
          <div className="bg-purple-100 p-3 rounded-lg">
            <Percent className="h-6 w-6 text-purple-600" />
          </div>
        </div>
      </div>
    </div>
  );

  const RecoveryCard = ({ recovery }) => {
    const recoveryData = recovery.workflow?.recovery || {};
    const disbursedAmount = recovery.workflow?.disbursal?.disbursedAmount || 0;
    const recoveredAmount = recoveryData.recoveredAmount || 0;
    const remainingAmount = disbursedAmount - recoveredAmount;
    const recoveryPercentage = disbursedAmount > 0 ? (recoveredAmount / disbursedAmount) * 100 : 0;
    const daysOverdue = getDaysOverdue(recovery.workflow?.disbursal?.disbursedDate);

    return (
      <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6 mb-4 hover:shadow-md transition-shadow">
        <div className="flex items-start justify-between">
          <div className="flex-1">
            <div className="flex items-center space-x-3 mb-3">
              <h3 className="text-lg font-semibold text-gray-900">{recovery.applicationId}</h3>
              {getStatusBadge(recoveryData.status || 'pending')}
              {recoveryData.insurance?.status && (
                <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-indigo-50 text-indigo-700">
                  Insurance: {String(recoveryData.insurance.status).replace(/_/g, ' ')}
                </span>
              )}
              {recoveryData.userDisbursal?.status === 'disbursed' && (
                <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-teal-50 text-teal-700">
                  User Paid
                </span>
              )}
              {recoveryData.lenderCommunication?.informed && (
                <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-cyan-50 text-cyan-700">
                  Lender Informed
                </span>
              )}
              {daysOverdue > 0 && (
                <span className="inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium bg-red-100 text-red-800">
                  {daysOverdue} days overdue
                </span>
              )}
            </div>

            <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
              {/* Patient & Insurance Info */}
              <div className="space-y-2">
                <h4 className="font-medium text-gray-900 flex items-center">
                  <User className="h-4 w-4 mr-2 text-teal-600" />
                  Patient & Insurance
                </h4>
                <div className="text-sm text-gray-600 space-y-1">
                  <div><strong>Patient:</strong> {recovery.patient?.name}</div>
                  <div><strong>Hospital:</strong> {recovery.hospitalName}</div>
                  <div><strong>Insurance:</strong> {recovery.policy?.insuranceCompany}</div>
                  <div><strong>Policy No:</strong> {recovery.policy?.number}</div>
                </div>
              </div>

              {/* Recovery Financial Details */}
              <div className="space-y-2">
                <h4 className="font-medium text-gray-900 flex items-center">
                  <DollarSign className="h-4 w-4 mr-2 text-teal-600" />
                  Recovery Details
                </h4>
                <div className="text-sm space-y-1">
                  <div className="text-blue-600">
                    <strong>Disbursed:</strong> {formatCurrency(disbursedAmount)}
                  </div>
                  {recoveredAmount > 0 && (
                    <div className="text-green-600">
                      <strong>Recovered:</strong> {formatCurrency(recoveredAmount)}
                    </div>
                  )}
                  <div className="text-red-600">
                    <strong>Remaining:</strong> {formatCurrency(remainingAmount)}
                  </div>
                  <div className="text-purple-600">
                    <strong>Recovery %:</strong> {recoveryPercentage.toFixed(1)}%
                  </div>
                  {recoveryData.userDisbursal?.status === 'disbursed' && (
                    <div className="text-teal-700">
                      <strong>User Disbursal:</strong> {formatCurrency(parseFloat(recoveryData.userDisbursal.amount || 0))} on {formatDate(recoveryData.userDisbursal.date)}
                    </div>
                  )}
                </div>
              </div>

              {/* Timeline & Status */}
              <div className="space-y-2">
                <h4 className="font-medium text-gray-900 flex items-center">
                  <Calendar className="h-4 w-4 mr-2 text-teal-600" />
                  Timeline
                </h4>
                <div className="text-sm text-gray-600 space-y-1">
                  <div><strong>Disbursed:</strong> {formatDate(recovery.workflow?.disbursal?.disbursedDate)}</div>
                  {recoveryData.recoveryDate && (
                    <div><strong>Last Recovery:</strong> {formatDate(recoveryData.recoveryDate)}</div>
                  )}
                  {recoveryData.insuranceResponseDate && (
                    <div><strong>Insurance Response:</strong> {formatDate(recoveryData.insuranceResponseDate)}</div>
                  )}
                  {recoveryData.insurance?.responseDate && (
                    <div><strong>Insurer Resp.:</strong> {formatDate(recoveryData.insurance.responseDate)}</div>
                  )}
                  {recoveryData.lenderCommunication?.informedAt && (
                    <div><strong>Lender Informed:</strong> {formatDate(recoveryData.lenderCommunication.informedAt)}</div>
                  )}
                  {recoveryData.enachDetails?.mandateId && (
                    <div className="text-blue-600">
                      <strong>eNACH:</strong> Active ({recoveryData.enachDetails.mandateId})
                    </div>
                  )}
                </div>
              </div>
            </div>

            {/* Recovery Progress Bar */}
            <div className="mt-4">
              <div className="flex justify-between items-center mb-1">
                <span className="text-sm font-medium text-gray-700">Recovery Progress</span>
                <span className="text-sm text-gray-600">{recoveryPercentage.toFixed(1)}%</span>
              </div>
              <div className="w-full bg-gray-200 rounded-full h-2">
                <div
                  className={`h-2 rounded-full ${
                    recoveryPercentage === 100 ? 'bg-green-600' :
                    recoveryPercentage > 50 ? 'bg-blue-600' :
                    recoveryPercentage > 25 ? 'bg-yellow-600' : 'bg-red-600'
                  }`}
                  style={{ width: `${Math.min(recoveryPercentage, 100)}%` }}
                ></div>
              </div>
            </div>
          </div>

          {/* Action Buttons */}
          <div className="flex flex-col space-y-2 ml-4">
            <button
              onClick={() => openUpdateModal(recovery)}
              className="flex items-center space-x-2 px-4 py-2 bg-teal-600 text-white rounded-lg hover:bg-teal-700 transition-colors text-sm"
            >
              <Edit className="h-4 w-4" />
              <span>Update Recovery</span>
            </button>
            
            <button
              onClick={() => setSelectedRecovery(recovery)}
              className="flex items-center space-x-2 px-4 py-2 bg-gray-600 text-white rounded-lg hover:bg-gray-700 transition-colors text-sm"
            >
              <Eye className="h-4 w-4" />
              <span>View Details</span>
            </button>

            {recoveryData.enachDetails?.mandateId && (
              <button
                className="flex items-center space-x-2 px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors text-sm"
              >
                <CreditCard className="h-4 w-4" />
                <span>eNACH Status</span>
              </button>
            )}
          </div>
        </div>
      </div>
    );
  };

  const UpdateModal = () => {
    if (!selectedRecovery) return null;

    return (
      <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
        <div className="bg-white rounded-lg shadow-xl max-w-6xl w-full max-h-[90vh] overflow-y-auto">
          <div className="p-6 border-b border-gray-200">
            <div className="flex items-center justify-between">
              <h2 className="text-xl font-semibold text-gray-900">
                Update Recovery Status
              </h2>
              <button
                onClick={() => setShowUpdateModal(false)}
                className="text-gray-400 hover:text-gray-600"
              >
                <XCircle className="h-6 w-6" />
              </button>
            </div>
            <p className="text-sm text-gray-600 mt-1">
              Application ID: {selectedRecovery.applicationId} | Patient: {selectedRecovery.patient?.name}
            </p>
          </div>

          <div className="p-6 space-y-6">
            {/* Recovery Status */}
            <div className="bg-blue-50 rounded-lg p-4">
              <h3 className="text-lg font-medium text-gray-900 mb-4">Recovery Status</h3>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Recovery Status</label>
                  <select
                    value={updateData.status}
                    onChange={(e) => setUpdateData(prev => ({ ...prev, status: e.target.value }))}
                    className="w-full border border-gray-300 rounded-md px-3 py-2 focus:ring-2 focus:ring-teal-500"
                  >
                    <option value="pending">Pending Recovery</option>
                    <option value="in_progress">In Progress</option>
                    <option value="partial_recovery">Partial Recovery</option>
                    <option value="completed">Completed</option>
                    <option value="overdue">Overdue</option>
                    <option value="disputed">Disputed</option>
                  </select>
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Recovered Amount</label>
                  <input
                    type="number"
                    value={updateData.recoveredAmount}
                    onChange={(e) => setUpdateData(prev => ({ ...prev, recoveredAmount: e.target.value }))}
                    className="w-full border border-gray-300 rounded-md px-3 py-2 focus:ring-2 focus:ring-teal-500"
                    placeholder="Enter recovered amount"
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Recovery Date</label>
                  <input
                    type="date"
                    value={updateData.recoveryDate}
                    onChange={(e) => setUpdateData(prev => ({ ...prev, recoveryDate: e.target.value }))}
                    className="w-full border border-gray-300 rounded-md px-3 py-2 focus:ring-2 focus:ring-teal-500"
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Insurance Response Date</label>
                  <input
                    type="date"
                    value={updateData.insuranceResponseDate}
                    onChange={(e) => setUpdateData(prev => ({ ...prev, insuranceResponseDate: e.target.value }))}
                    className="w-full border border-gray-300 rounded-md px-3 py-2 focus:ring-2 focus:ring-teal-500"
                  />
                </div>
              </div>
            </div>

            {/* eNACH Details */}
            <div className="bg-green-50 rounded-lg p-4">
              <h3 className="text-lg font-medium text-gray-900 mb-4">eNACH Setup</h3>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">eNACH Status</label>
                  <select
                    value={updateData.enachStatus}
                    onChange={(e) => setUpdateData(prev => ({ ...prev, enachStatus: e.target.value }))}
                    className="w-full border border-gray-300 rounded-md px-3 py-2 focus:ring-2 focus:ring-teal-500"
                  >
                    <option value="">Not Applicable</option>
                    <option value="pending">Pending Setup</option>
                    <option value="active">Active</option>
                    <option value="completed">Completed</option>
                    <option value="failed">Failed</option>
                    <option value="cancelled">Cancelled</option>
                  </select>
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Mandate ID</label>
                  <input
                    type="text"
                    value={updateData.enachDetails.mandateId}
                    onChange={(e) => setUpdateData(prev => ({
                      ...prev,
                      enachDetails: { ...prev.enachDetails, mandateId: e.target.value }
                    }))}
                    className="w-full border border-gray-300 rounded-md px-3 py-2 focus:ring-2 focus:ring-teal-500"
                    placeholder="Enter mandate ID"
                  />
                </div>
              </div>

              {updateData.enachStatus && updateData.enachStatus !== '' && (
                <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">Bank Name</label>
                    <input
                      type="text"
                      value={updateData.enachDetails.bankName}
                      onChange={(e) => setUpdateData(prev => ({
                        ...prev,
                        enachDetails: { ...prev.enachDetails, bankName: e.target.value }
                      }))}
                      className="w-full border border-gray-300 rounded-md px-3 py-2 focus:ring-2 focus:ring-teal-500"
                      placeholder="Enter bank name"
                    />
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">Monthly Amount</label>
                    <input
                      type="number"
                      value={updateData.enachDetails.monthlyAmount}
                      onChange={(e) => setUpdateData(prev => ({
                        ...prev,
                        enachDetails: { ...prev.enachDetails, monthlyAmount: e.target.value }
                      }))}
                      className="w-full border border-gray-300 rounded-md px-3 py-2 focus:ring-2 focus:ring-teal-500"
                      placeholder="Monthly deduction amount"
                    />
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">Total Installments</label>
                    <input
                      type="number"
                      value={updateData.enachDetails.totalInstallments}
                      onChange={(e) => setUpdateData(prev => ({
                        ...prev,
                        enachDetails: { ...prev.enachDetails, totalInstallments: e.target.value }
                      }))}
                      className="w-full border border-gray-300 rounded-md px-3 py-2 focus:ring-2 focus:ring-teal-500"
                      placeholder="Number of installments"
                    />
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">Start Date</label>
                    <input
                      type="date"
                      value={updateData.enachDetails.startDate}
                      onChange={(e) => setUpdateData(prev => ({
                        ...prev,
                        enachDetails: { ...prev.enachDetails, startDate: e.target.value }
                      }))}
                      className="w-full border border-gray-300 rounded-md px-3 py-2 focus:ring-2 focus:ring-teal-500"
                    />
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">End Date</label>
                    <input
                      type="date"
                      value={updateData.enachDetails.endDate}
                      onChange={(e) => setUpdateData(prev => ({
                        ...prev,
                        enachDetails: { ...prev.enachDetails, endDate: e.target.value }
                      }))}
                      className="w-full border border-gray-300 rounded-md px-3 py-2 focus:ring-2 focus:ring-teal-500"
                    />
                  </div>
                </div>
              )}
            </div>

            {/* Insurance Process */}
            <div className="bg-indigo-50 rounded-lg p-4">
              <h3 className="text-lg font-medium text-gray-900 mb-4">Insurance Process</h3>
              <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Insurance Status</label>
                  <select
                    value={updateData.insurance.status}
                    onChange={(e) => setUpdateData(prev => ({ ...prev, insurance: { ...prev.insurance, status: e.target.value } }))}
                    className="w-full border border-gray-300 rounded-md px-3 py-2 focus:ring-2 focus:ring-teal-500"
                  >
                    <option value="">Select</option>
                    <option value="under_process">Under Process by Insurance</option>
                    <option value="approved">Approved</option>
                    <option value="rejected">Rejected</option>
                  </select>
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Claim Number</label>
                  <input
                    type="text"
                    value={updateData.insurance.claimNumber}
                    onChange={(e) => setUpdateData(prev => ({ ...prev, insurance: { ...prev.insurance, claimNumber: e.target.value } }))}
                    className="w-full border border-gray-300 rounded-md px-3 py-2"
                    placeholder="Insurer claim/reference"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Amount Approved</label>
                  <input
                    type="number"
                    value={updateData.insurance.amountApproved}
                    onChange={(e) => setUpdateData(prev => ({ ...prev, insurance: { ...prev.insurance, amountApproved: e.target.value } }))}
                    className="w-full border border-gray-300 rounded-md px-3 py-2"
                    placeholder="₹"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Response Date</label>
                  <input
                    type="date"
                    value={updateData.insurance.responseDate}
                    onChange={(e) => setUpdateData(prev => ({ ...prev, insurance: { ...prev.insurance, responseDate: e.target.value } }))}
                    className="w-full border border-gray-300 rounded-md px-3 py-2"
                  />
                </div>
                <div className="md:col-span-2">
                  <label className="block text-sm font-medium text-gray-700 mb-1">Insurance Notes</label>
                  <input
                    type="text"
                    value={updateData.insurance.notes}
                    onChange={(e) => setUpdateData(prev => ({ ...prev, insurance: { ...prev.insurance, notes: e.target.value } }))}
                    className="w-full border border-gray-300 rounded-md px-3 py-2"
                    placeholder="Notes about insurer process"
                  />
                </div>
              </div>
            </div>

            {/* Amount Disbursed to User */}
            <div className="bg-teal-50 rounded-lg p-4">
              <h3 className="text-lg font-medium text-gray-900 mb-4">Amount Disbursed to User</h3>
              <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Status</label>
                  <select
                    value={updateData.userDisbursal.status}
                    onChange={(e) => setUpdateData(prev => ({ ...prev, userDisbursal: { ...prev.userDisbursal, status: e.target.value } }))}
                    className="w-full border border-gray-300 rounded-md px-3 py-2"
                  >
                    <option value="">Select</option>
                    <option value="disbursed">Disbursed</option>
                    <option value="pending">Pending</option>
                  </select>
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Amount</label>
                  <input
                    type="number"
                    value={updateData.userDisbursal.amount}
                    onChange={(e) => setUpdateData(prev => ({ ...prev, userDisbursal: { ...prev.userDisbursal, amount: e.target.value } }))}
                    className="w-full border border-gray-300 rounded-md px-3 py-2"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Date</label>
                  <input
                    type="date"
                    value={updateData.userDisbursal.date}
                    onChange={(e) => setUpdateData(prev => ({ ...prev, userDisbursal: { ...prev.userDisbursal, date: e.target.value } }))}
                    className="w-full border border-gray-300 rounded-md px-3 py-2"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Mode</label>
                  <input
                    type="text"
                    value={updateData.userDisbursal.mode}
                    onChange={(e) => setUpdateData(prev => ({ ...prev, userDisbursal: { ...prev.userDisbursal, mode: e.target.value } }))}
                    className="w-full border border-gray-300 rounded-md px-3 py-2"
                    placeholder="NEFT/UPI/IMPS"
                  />
                </div>
                <div className="md:col-span-2">
                  <label className="block text-sm font-medium text-gray-700 mb-1">Reference</label>
                  <input
                    type="text"
                    value={updateData.userDisbursal.reference}
                    onChange={(e) => setUpdateData(prev => ({ ...prev, userDisbursal: { ...prev.userDisbursal, reference: e.target.value } }))}
                    className="w-full border border-gray-300 rounded-md px-3 py-2"
                    placeholder="Txn reference"
                  />
                </div>
              </div>
            </div>

            {/* Lender Communication */}
            <div className="bg-cyan-50 rounded-lg p-4">
              <h3 className="text-lg font-medium text-gray-900 mb-4">Lender Communication</h3>
              <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
                <div className="flex items-center space-x-2">
                  <input
                    type="checkbox"
                    checked={updateData.lenderCommunication.informed}
                    onChange={(e) => setUpdateData(prev => ({ ...prev, lenderCommunication: { ...prev.lenderCommunication, informed: e.target.checked } }))}
                    className="rounded border-gray-300 text-teal-600"
                  />
                  <span className="text-sm text-gray-700">Informed Lender</span>
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Informed At</label>
                  <input
                    type="datetime-local"
                    value={updateData.lenderCommunication.informedAt}
                    onChange={(e) => setUpdateData(prev => ({ ...prev, lenderCommunication: { ...prev.lenderCommunication, informedAt: e.target.value } }))}
                    className="w-full border border-gray-300 rounded-md px-3 py-2"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Contact Person</label>
                  <input
                    type="text"
                    value={updateData.lenderCommunication.contactPerson}
                    onChange={(e) => setUpdateData(prev => ({ ...prev, lenderCommunication: { ...prev.lenderCommunication, contactPerson: e.target.value } }))}
                    className="w-full border border-gray-300 rounded-md px-3 py-2"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Channel</label>
                  <input
                    type="text"
                    value={updateData.lenderCommunication.channel}
                    onChange={(e) => setUpdateData(prev => ({ ...prev, lenderCommunication: { ...prev.lenderCommunication, channel: e.target.value } }))}
                    className="w-full border border-gray-300 rounded-md px-3 py-2"
                    placeholder="Email/Call/Portal"
                  />
                </div>
                <div className="md:col-span-2">
                  <label className="block text-sm font-medium text-gray-700 mb-1">Notes</label>
                  <input
                    type="text"
                    value={updateData.lenderCommunication.notes}
                    onChange={(e) => setUpdateData(prev => ({ ...prev, lenderCommunication: { ...prev.lenderCommunication, notes: e.target.value } }))}
                    className="w-full border border-gray-300 rounded-md px-3 py-2"
                  />
                </div>
                <div className="flex items-center space-x-2">
                  <input
                    type="checkbox"
                    checked={updateData.lenderCommunication.enachInitiate}
                    onChange={(e) => setUpdateData(prev => ({ ...prev, lenderCommunication: { ...prev.lenderCommunication, enachInitiate: e.target.checked } }))}
                    className="rounded border-gray-300 text-teal-600"
                  />
                  <span className="text-sm text-gray-700">Tell lender to initiate eNACH</span>
                </div>
              </div>
            </div>

            {/* Follow-up Actions */}
            <div className="bg-yellow-50 rounded-lg p-4">
              <div className="flex items-center justify-between mb-4">
                <h3 className="text-lg font-medium text-gray-900">Follow-up Actions</h3>
                <button
                  onClick={addFollowUpAction}
                  className="flex items-center space-x-1 px-3 py-2 bg-yellow-600 text-white rounded-md hover:bg-yellow-700 text-sm"
                >
                  <FileText className="h-4 w-4" />
                  <span>Add Follow-up</span>
                </button>
              </div>

              {updateData.followUpActions.length === 0 ? (
                <p className="text-gray-600 text-center py-4">No follow-up actions recorded</p>
              ) : (
                <div className="space-y-3">
                  {updateData.followUpActions.map((action) => (
                    <div key={action.id} className="bg-white p-4 rounded-lg border border-yellow-200">
                      <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                        <div>
                          <label className="block text-sm font-medium text-gray-700 mb-1">Date</label>
                          <input
                            type="date"
                            value={action.date}
                            onChange={(e) => updateFollowUpAction(action.id, 'date', e.target.value)}
                            className="w-full border border-gray-300 rounded-md px-3 py-2 text-sm"
                          />
                        </div>

                        <div>
                          <label className="block text-sm font-medium text-gray-700 mb-1">Action Type</label>
                          <select
                            value={action.action}
                            onChange={(e) => updateFollowUpAction(action.id, 'action', e.target.value)}
                            className="w-full border border-gray-300 rounded-md px-3 py-2 text-sm"
                          >
                            <option value="">Select action</option>
                            <option value="phone_call">Phone Call</option>
                            <option value="email_sent">Email Sent</option>
                            <option value="letter_sent">Letter Sent</option>
                            <option value="meeting_scheduled">Meeting Scheduled</option>
                            <option value="legal_notice">Legal Notice</option>
                            <option value="enach_setup">eNACH Setup</option>
                            <option value="payment_received">Payment Received</option>
                            <option value="other">Other</option>
                          </select>
                        </div>

                        <div>
                          <label className="block text-sm font-medium text-gray-700 mb-1">Next Follow-up</label>
                          <input
                            type="date"
                            value={action.nextFollowUp}
                            onChange={(e) => updateFollowUpAction(action.id, 'nextFollowUp', e.target.value)}
                            className="w-full border border-gray-300 rounded-md px-3 py-2 text-sm"
                          />
                        </div>

                        <div className="md:col-span-2">
                          <label className="block text-sm font-medium text-gray-700 mb-1">Notes</label>
                          <textarea
                            value={action.notes}
                            onChange={(e) => updateFollowUpAction(action.id, 'notes', e.target.value)}
                            rows={2}
                            className="w-full border border-gray-300 rounded-md px-3 py-2 text-sm"
                            placeholder="Enter notes about this follow-up action"
                          />
                        </div>

                        <div className="flex items-end">
                          <button
                            onClick={() => removeFollowUpAction(action.id)}
                            className="w-full px-3 py-2 bg-red-600 text-white rounded-md hover:bg-red-700 text-sm"
                          >
                            Remove
                          </button>
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </div>

            {/* Recovery Notes */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Recovery Notes</label>
              <textarea
                value={updateData.recoveryNotes}
                onChange={(e) => setUpdateData(prev => ({ ...prev, recoveryNotes: e.target.value }))}
                rows={4}
                className="w-full border border-gray-300 rounded-md px-3 py-2 focus:ring-2 focus:ring-teal-500"
                placeholder="Add notes about recovery progress, challenges, or next steps..."
              />
            </div>
          </div>

          <div className="p-6 border-t border-gray-200 flex justify-end space-x-3">
            <button
              onClick={() => setShowUpdateModal(false)}
              className="px-4 py-2 text-gray-700 bg-gray-200 rounded-lg hover:bg-gray-300 transition-colors"
            >
              Cancel
            </button>
            <button
              onClick={handleUpdateSubmit}
              className="px-6 py-2 bg-teal-600 text-white rounded-lg hover:bg-teal-700 transition-colors"
            >
              Update Recovery
            </button>
          </div>
        </div>
      </div>
    );
  };

  if (loading) {
    return (
      <div className="space-y-6">
        <div className="animate-pulse">
          <div className="h-8 bg-gray-200 rounded w-1/4 mb-4"></div>
          <div className="grid grid-cols-5 gap-4 mb-6">
            {[...Array(5)].map((_, i) => (
              <div key={i} className="h-24 bg-gray-200 rounded"></div>
            ))}
          </div>
          {[...Array(3)].map((_, i) => (
            <div key={i} className="h-32 bg-gray-200 rounded mb-4"></div>
          ))}
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">Recovery Tracking</h1>
          <p className="text-gray-600">Monitor and manage insurance claim recoveries and eNACH setups</p>
        </div>
        <div className="flex items-center space-x-3">
          <span className="text-sm text-gray-600">
            {recoveries.length} recovery cases
          </span>
          <button
            onClick={loadRecoveries}
            className="flex items-center space-x-2 px-4 py-2 bg-teal-600 text-white rounded-lg hover:bg-teal-700 transition-colors"
          >
            <RefreshCw className="h-4 w-4" />
            <span>Refresh</span>
          </button>
        </div>
      </div>

      {/* Stats Cards */}
      <StatsCards />

      {/* Filters */}
      <div className="bg-white p-4 rounded-lg shadow-sm border border-gray-200">
        <div className="grid grid-cols-1 md:grid-cols-5 gap-4">
          <div className="relative">
            <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-gray-400" />
            <input
              type="text"
              placeholder="Search applications..."
              value={filters.searchTerm}
              onChange={(e) => setFilters(prev => ({ ...prev, searchTerm: e.target.value }))}
              className="pl-10 pr-4 py-2 w-full border border-gray-300 rounded-lg focus:ring-2 focus:ring-teal-500"
            />
          </div>

          <select
            value={filters.status}
            onChange={(e) => setFilters(prev => ({ ...prev, status: e.target.value }))}
            className="border border-gray-300 rounded-lg px-3 py-2 focus:ring-2 focus:ring-teal-500"
          >
            <option value="">All Recovery Status</option>
            <option value="pending">Pending</option>
            <option value="in_progress">In Progress</option>
            <option value="partial_recovery">Partial Recovery</option>
            <option value="completed">Completed</option>
            <option value="overdue">Overdue</option>
          </select>

          <select
            value={filters.insuranceCompany}
            onChange={(e) => setFilters(prev => ({ ...prev, insuranceCompany: e.target.value }))}
            className="border border-gray-300 rounded-lg px-3 py-2 focus:ring-2 focus:ring-teal-500"
          >
            <option value="">All Insurance Companies</option>
            <option value="HDFC ERGO">HDFC ERGO</option>
            <option value="Star Health">Star Health</option>
            <option value="Bajaj Allianz">Bajaj Allianz</option>
            <option value="ICICI Lombard">ICICI Lombard</option>
            <option value="Oriental Insurance">Oriental Insurance</option>
          </select>

          <input
            type="date"
            value={filters.dateFrom}
            onChange={(e) => setFilters(prev => ({ ...prev, dateFrom: e.target.value }))}
            className="border border-gray-300 rounded-lg px-3 py-2 focus:ring-2 focus:ring-teal-500"
            placeholder="From Date"
          />

          <div className="flex items-center space-x-2">
            <label className="flex items-center">
              <input
                type="checkbox"
                checked={filters.overdueOnly}
                onChange={(e) => setFilters(prev => ({ ...prev, overdueOnly: e.target.checked }))}
                className="rounded border-gray-300 text-teal-600 focus:ring-teal-500"
              />
              <span className="ml-2 text-sm text-gray-700">Overdue Only</span>
            </label>
          </div>
        </div>
      </div>

      {/* Recovery List */}
      {recoveries.length > 0 ? (
        <div className="space-y-4">
          {recoveries.map((recovery) => (
            <RecoveryCard key={recovery.id} recovery={recovery} />
          ))}
        </div>
      ) : (
        <div className="text-center py-12 bg-white rounded-lg shadow-sm">
          <Banknote className="h-12 w-12 text-gray-400 mx-auto mb-4" />
          <h3 className="text-lg font-medium text-gray-900 mb-2">No Recovery Cases Found</h3>
          <p className="text-gray-600">
            {Object.values(filters).some(v => v !== '' && v !== false) 
              ? 'Try adjusting your filters to see more recovery cases.'
              : 'No disbursed applications require recovery tracking at the moment.'
            }
          </p>
        </div>
      )}

      {/* Update Modal */}
      {showUpdateModal && selectedRecovery && (
        <UpdateRecoveryModal
          selectedRecovery={selectedRecovery}
          updateData={updateData}
          setUpdateData={setUpdateData}
          onClose={handleModalClose}
          onSubmit={handleUpdateSubmit}
          addFollowUpAction={addFollowUpAction}
          updateFollowUpAction={updateFollowUpAction}
          removeFollowUpAction={removeFollowUpAction}
        />
      )}
    </div>
  );
};

export default RecoveryTracking;